// ============================================
// LaTeX Renderer — Shared Utility (Advanced)
// Migrated from PHP: formatLatexForMathJax() + ensureCompleteTeXEnv()
// Enhanced with 100+ TeX commands, chemistry, logic, decorations
// ============================================

/**
 * Normalize HTML containing LaTeX for MathJax v3 rendering.
 * Converts $...$ → \(...\), $$...$$ → \[...\], wraps bare TeX, repairs broken environments.
 * Handles mixed content (text + math), currency protection, Word artifacts,
 * chemistry notation (\ce{}, \pu{}), and 15+ environment types.
 */
export function normalizeLatex(html: string | null | undefined): string {
  if (!html || typeof html !== 'string') return '';
  let t = html;

  // 1. Normalize NBSP variants (from PHP: str_replace)
  t = t.replace(/\xC2\xA0/g, ' ');
  t = t.replace(/\xA0/g, ' ');
  t = t.replace(/&nbsp;/gi, ' ');

  // 2. Remove common Word/editor spacing artifacts inside TeX (from PHP)
  t = t.replace(/\\!\\!~\\!\\!/g, ' ');
  t = t.replace(/\\mbox\{\s*\}/g, '');
  t = t.replace(/\\hbox\{\s*\}/g, '');
  t = t.replace(/\\text\{\s*\}/g, '');
  // Additional Word cleanup: zero-width spaces, BOM
  t = t.replace(/[\u200B\u200C\u200D\uFEFF]/g, '');
  // Remove Word-generated style spans that break math rendering
  t = t.replace(/<span\s+style="[^"]*mso-[^"]*"[^>]*>([\s\S]*?)<\/span>/gi, '$1');

  // 2b. Strip inline formatting spans (highlight, color, font) that wrap around LaTeX
  //     These cause MathJax to skip the content because it's inside styled HTML elements.
  //     e.g. <span style="background:yellow">\begin{matrix}...\end{matrix}</span>
  //     Only strip spans whose content contains TeX commands (preserve spans around plain text)
  t = t.replace(/<span\s+style="[^"]*(?:background|color|font)[^"]*"[^>]*>([\s\S]*?)<\/span>/gi,
    (match, inner) => TEX_COMMAND_RE.test(inner) || /[_^]\{/.test(inner) ? inner : match
  );

  // 2c. Decode HTML entities that break LaTeX — CKEditor stores & as &amp;
  //     Matrix column separators: a &amp; 2 &amp; 1 → a & 2 & 1
  //     Must happen early, before delimiter detection/wrapping
  //     NOTE: ONLY decode &amp; and &quot; globally — these are safe for dangerouslySetInnerHTML.
  //     Do NOT decode &lt;/&gt; here — "a &lt; b" would become "a < b" which the browser
  //     parses as an HTML tag opener, breaking the display. &lt;/&gt; are decoded ONLY
  //     inside math delimiters (step 11) where they can't create fake HTML tags.
  t = t.replace(/&amp;/g, '&');
  t = t.replace(/&quot;/g, '"');

  // 3. Detect existing delimiters
  const hasModernDelimiters = /\\\([\s\S]*?\\\)/.test(t) || /\\\[[\s\S]*?\\\]/.test(t);
  const hasDollarMath = /\$/.test(t);

  // 4. Convert dollar-sign math to \(\) and \[\] delimiters
  if (hasDollarMath) {
    // 4a. Convert display math FIRST: $$...$$ → \[ ... \]
    // MUST happen before spacing cleanup to prevent $$ from being split
    t = t.replace(/\$\$([\s\S]*?)\$\$/g, '\\[$1\\]');

    // 4b. Ensure space between adjacent math blocks (after $$ are converted)
    // Handles cases like \)\( or \]\[ where two math blocks touch
    t = t.replace(/(\\\\[)\]])(?=\\\\[(\[])/g, '$1 ');

    // 4c. Convert inline math: $...$ → \( ... \)
    // Match all $...$ pairs. Only skip if content is purely a currency amount (digits/commas/dots only).
    // IMPORTANT: Do NOT use (?!\d) lookahead — it breaks math like $60^{\circ}$
    t = t.replace(/\$(?!\$)([\s\S]*?)\$/g, (match, content) => {
      if (!content || !content.trim()) return match;
      // Skip if it looks like a price (entire content is just digits, commas, dots, spaces — no TeX commands)
      if (/^\s*[\d,.\s]+[KkMmBb]?\s*$/.test(content)) return match;
      return `\\(${content}\\)`;
    });
  }

  // 5. Repair broken TeX environments (from PHP ensureCompleteTeXEnv, expanded)
  const envNames = [
    'array', 'cases', 'matrix', 'pmatrix', 'bmatrix', 'vmatrix', 'Vmatrix',
    'aligned', 'align\\*', 'gather', 'gathered', 'split', 'equation\\*',
    'tabular', 'smallmatrix', 'eqnarray', 'eqnarray\\*', 'subarray',
    'multline', 'multline\\*', 'flalign', 'flalign\\*'
  ];
  for (const env of envNames) {
    const envClean = env.replace('\\*', '*');
    const beginRe = new RegExp(`\\\\begin\\{${env}\\}`, 'g');
    const endRe = new RegExp(`\\\\end\\{${env}\\}`, 'g');
    const beginCount = (t.match(beginRe) || []).length;
    const endCount = (t.match(endRe) || []).length;
    if (beginCount > endCount) {
      for (let i = 0; i < beginCount - endCount; i++) t += `\\end{${envClean}}`;
    }
    if (endCount > beginCount) {
      for (let i = 0; i < endCount - beginCount; i++) t = `\\begin{${envClean}}` + t;
    }
  }

  // 6. Ensure \begin{} environments are wrapped in math delimiters
  // MathJax v3 requires environments inside delimiters to render them
  const mathEnvNames = ['array','cases','matrix','pmatrix','bmatrix','vmatrix',
                        'Vmatrix','aligned','gathered','split','smallmatrix'];
  for (const env of mathEnvNames) {
    // Match \begin{env}...\end{env} — in the string, \\ = literal backslash
    const re = new RegExp(
      `(\\\\begin\\{${env}\\}[\\s\\S]*?\\\\end\\{${env}\\})`, 'g'
    );
    t = t.replace(re, (match, _p1, offset) => {
      // Check if already inside math delimiters by counting unmatched \[ and \( in the prefix.
      // A simple 5-char lookbehind fails when e.g. \left| sits between \[ and \begin{matrix}.
      const prefix = t.substring(0, offset);
      const openDisplay = (prefix.match(/\\\[/g) || []).length - (prefix.match(/\\\]/g) || []).length;
      const openInline = (prefix.match(/\\\(/g) || []).length - (prefix.match(/\\\)/g) || []).length;
      if (openDisplay > 0 || openInline > 0) return match;  // already inside delimiters
      return `\\[${match}\\]`;
    });
  }

  // 7. Heuristic: wrap bare TeX (commands without delimiters) — from PHP, expanded
  // Split by <br>, <p>, <div>, <li> tags and process each segment
  const alreadyHasDelimiters = /\\\(/.test(t) || /\\\[/.test(t);
  if (!alreadyHasDelimiters) {
    const parts = t.split(/(<br\s*\/?\s*>|<\/?p[^>]*>|<\/?div[^>]*>|<\/?li[^>]*>)/i);
    for (let i = 0; i < parts.length; i += 2) {
      const seg = parts[i];
      if (!seg || seg.trim() === '') continue;
      // Skip if contains images or iframes
      if (/<img/i.test(seg) || /<iframe/i.test(seg)) continue;
      // Skip if already has math delimiters
      if (/\\\(/.test(seg) || /\\\)/.test(seg) || /\\\[/.test(seg) || /\\\]/.test(seg)) continue;

      // Detect common TeX commands or sub/superscript braces (100+ commands)
      const hasTex = TEX_COMMAND_RE.test(seg) || /[_^]\{/.test(seg);
      if (hasTex) {
        const isDisplay = DISPLAY_ENV_RE.test(seg) || /\\\\/.test(seg);
        parts[i] = isDisplay ? `\\[${seg}\\]` : `\\(${seg}\\)`;
      }
    }
    t = parts.join('');
  } else {
    // Mixed content: some segments have delimiters, some have bare TeX
    // Process only segments that are NOT inside existing \( \) or \[ \]
    t = t.replace(/((?:^|\\[)\]]))([^\\]*?)(?=\\[(\[]|$)/g, (match, before, between) => {
      if (!between || !between.trim()) return match;
      if (/<img/i.test(between)) return match;
      const hasTex = TEX_COMMAND_RE.test(between) || /[_^]\{/.test(between);
      if (hasTex) {
        const isDisplay = DISPLAY_ENV_RE.test(between) || /\\\\/.test(between);
        return before + (isDisplay ? `\\[${between}\\]` : `\\(${between}\\)`);
      }
      return match;
    });
  }

  // 8. Prevent double-wrapping: \(\(...\)\) or \[\[...\]\]
  t = t.replace(/\\\(\s*\\\(/g, '\\(');
  t = t.replace(/\\\)\s*\\\)/g, '\\)');
  t = t.replace(/\\\[\s*\\\[/g, '\\[');
  t = t.replace(/\\\]\s*\\\]/g, '\\]');

  // 9. Fix common MathJax rendering issues — CONSERVATIVE repairs only
  // IMPORTANT: In LaTeX, space between command and arg is valid:
  //   \sqrt {x} = \sqrt{x}, \frac{a} {b} = \frac{a}{b}, \sqrt [3]{x} = \sqrt[3]{x}
  //   \sqrt x = √x (takes next token as arg), \frac ab = a/b
  // Only repair truly orphaned commands at end-of-block with NO following content.

  // 9a. Repair \frac with only one arg AND no second arg following (even with whitespace)
  // Match \frac{...} NOT followed by optional-whitespace + {
  // Also handle nested braces: use a safer pattern that stops at balanced }
  t = t.replace(/\\frac\{([^}]*)\}(?!\s*\{)/g, '\\frac{$1}{}');

  // 9b. Repair truly bare \sqrt — NOT followed by whitespace+brace, whitespace+bracket,
  // or any non-whitespace token (which LaTeX takes as the argument).
  // Only match \sqrt at end of string or followed by delimiter/closing
  t = t.replace(/\\sqrt(?!\s*[\{\[a-zA-Z0-9\\])/g, '\\sqrt{}');

  // 10. Clean up accidental doubled spaces — but ONLY outside math delimiters
  // Protect math regions: collapsing spaces inside $...$, \(...\), \[...\] can break alignment
  t = t.replace(/(\\\\[(\[][\\s\\S]*?\\\\[)\]])|[ \t]{2,}/g, (match, mathBlock) => {
    if (mathBlock) return match; // Preserve math regions as-is
    return ' '; // Collapse non-math doubled spaces
  });

  // 11. Decode any remaining HTML entities inside math delimiters
  //     (Belt-and-suspenders — step 2c handles most, but catch any introduced by wrapping)
  t = t.replace(/\\\([\s\S]*?\\\)/g, (match) => {
    return match.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
  });
  t = t.replace(/\\\[[\s\S]*?\\\]/g, (match) => {
    return match.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
  });

  return t;
}

// Extended TeX command pattern (100+ commands — covers all standard math, physics, chemistry)
const TEX_COMMAND_RE = /\\(begin|end|frac|dfrac|tfrac|cfrac|sqrt|left|right|text|textbf|textit|textrm|textsc|mathrm|mathbf|mathit|mathbb|mathcal|mathfrak|mathscr|mathsf|boldsymbol|sin|cos|tan|cot|sec|csc|arcsin|arccos|arctan|arcctg|sinh|cosh|tanh|coth|sech|csch|pi|alpha|beta|gamma|Gamma|delta|Delta|epsilon|varepsilon|theta|Theta|vartheta|lambda|Lambda|mu|sigma|Sigma|omega|Omega|phi|Phi|varphi|psi|Psi|rho|varrho|tau|eta|zeta|xi|Xi|nu|kappa|chi|iota|upsilon|Upsilon|int|iint|iiint|oint|oiint|sum|prod|coprod|lim|limsup|liminf|log|ln|exp|max|min|sup|inf|det|dim|ker|gcd|hom|arg|deg|Pr|cdot|cdots|ldots|vdots|ddots|times|div|pm|mp|ast|star|circ|bullet|le|ge|leq|geq|neq|ne|approx|equiv|cong|sim|simeq|doteq|propto|subset|supset|subseteq|supseteq|nsubseteq|nsupseteq|in|notin|ni|cup|cap|setminus|emptyset|varnothing|forall|exists|nexists|nabla|partial|infty|aleph|beth|wp|Re|Im|hbar|ell|mho|because|therefore|angle|measuredangle|triangle|square|lozenge|perp|parallel|nparallel|vdash|dashv|models|cong|propto|to|rightarrow|leftarrow|leftrightarrow|Rightarrow|Leftarrow|Leftrightarrow|longrightarrow|longleftarrow|implies|impliedby|iff|mapsto|longmapsto|hookrightarrow|hookleftarrow|nearrow|searrow|swarrow|nwarrow|uparrow|downarrow|Uparrow|Downarrow|updownarrow|neg|lnot|land|lor|oplus|otimes|odot|ominus|oslash|bigcup|bigcap|bigoplus|bigotimes|bigsqcup|bigvee|bigwedge|vec|hat|bar|dot|ddot|tilde|acute|grave|check|breve|overline|underline|overrightarrow|overleftarrow|widehat|widetilde|overbrace|underbrace|binom|dbinom|tbinom|choose|stackrel|overset|underset|xleftarrow|xrightarrow|xLeftarrow|xRightarrow|ce|pu|hline|cline|multicolumn|quad|qquad|enspace|thinspace|hspace|vspace|kern|mkern|displaystyle|textstyle|scriptstyle|scriptscriptstyle|color|textcolor|colorbox|fcolorbox|boxed|fbox|cancel|bcancel|xcancel|sout|not|mod|pmod|bmod|operatorname|DeclareMathOperator|limits|nolimits|atop|above|over|underbracket|overbracket|xlongequal|dagger|ddagger|langle|rangle|lceil|rceil|lfloor|rfloor|vert|Vert||)\b/;

const DISPLAY_ENV_RE = /\\begin\{(array|matrix|pmatrix|bmatrix|vmatrix|Vmatrix|cases|aligned|align\*|gather|gathered|equation\*|tabular|smallmatrix|eqnarray|multline|split)\}/;

/**
 * Check if a string contains any LaTeX content.
 * Used for conditional rendering decisions.
 */
export function containsLatex(text: string | null | undefined): boolean {
  if (!text) return false;
  return TEX_COMMAND_RE.test(text) || /\$/.test(text) || /\\\(/.test(text) || /\\\[/.test(text) || /[_^]\{/.test(text);
}

/**
 * Strip all LaTeX delimiters and commands for plain-text search/indexing.
 * Returns human-readable approximation of the math content.
 */
export function stripLatex(text: string | null | undefined): string {
  if (!text) return '';
  let t = text;
  // Remove display math delimiters
  t = t.replace(/\\\[/g, '').replace(/\\\]/g, '');
  t = t.replace(/\$\$/g, '');
  // Remove inline math delimiters
  t = t.replace(/\\\(/g, '').replace(/\\\)/g, '');
  t = t.replace(/\$/g, '');
  // Remove \begin{} and \end{} blocks
  t = t.replace(/\\(begin|end)\{[^}]*\}/g, '');
  // Remove common commands but keep content
  t = t.replace(/\\(frac|sqrt|text|textbf|textit|mathbf|mathrm|mathbb|overline|underline|vec|hat|bar|tilde|operatorname)\{/g, '{');
  // Remove remaining commands
  t = t.replace(/\\[a-zA-Z]+/g, ' ');
  // Remove braces
  t = t.replace(/[{}]/g, '');
  // Remove HTML tags
  t = t.replace(/<[^>]+>/g, ' ');
  // Clean whitespace
  t = t.replace(/\s+/g, ' ').trim();
  return t;
}

/**
 * Rewrite Word-imported image paths to absolute URLs.
 * Converts ./uploads/, ../uploads/ patterns to proper base URL paths.
 * Also fixes broken image src attributes from Word HTML export.
 */
export function rewriteImagePaths(html: string, baseUrl?: string): string {
  if (!html) return '';
  const base = baseUrl || '';
  let t = html;
  // Rewrite upload paths: normalize any ../, ./, /, // prefix before 'uploads/' then add base + /
  // Step 1: In src attributes, strip any leading slashes/dots before 'uploads/' and prepend base/
  t = t.replace(/(src\s*=\s*["'])(?:\.\.\/|\.\/|\/+)*(uploads\/)/gi, `$1${base}/uploads/`);
  // Step 2: Catch bare 'uploads/' in src (no prefix at all)
  t = t.replace(/(src\s*=\s*["'])(?!\/|https?:\/\/)(uploads\/)/gi, `$1${base}/uploads/`);
  // Fix Word-exported image paths with file:/// protocol
  t = t.replace(/src="file:\/\/\/[^"]*[\/\\]([^"\/\\]+)"/gi, `src="${base}/uploads/editor_images/$1"`);
  // Safety: fix any remaining double slashes (but not in http:// or https://)
  t = t.replace(/([^:])\/\//g, '$1/');
  return t;
}

/**
 * Convert Markdown image syntax ![alt](url) to <img> HTML tags.
 * Handles Mathpix CDN URLs with dimension query params for smart sizing.
 * Should be called AFTER normalizeLatex and rewriteImagePaths.
 */
export function convertMarkdownImages(html: string): string {
  if (!html) return '';
  return html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_, alt: string, u: string) => {
    let src = u;
    // Parse optional Mathpix query params for sizing
    let widthStyle = 'max-width:100%;height:auto;';
    const heightMatch = u.match(/[?&]height=(\d+)/);
    const widthMatch = u.match(/[?&]width=(\d+)/);
    if (heightMatch && widthMatch) {
      const w = parseInt(widthMatch[1]);
      // Use actual pixel dimensions capped at reasonable max
      widthStyle = `max-width:100%;height:auto;width:${Math.min(w, 400)}px;`;
    }
    // Fix relative paths
    if (src && src[0] !== '/' && !src.startsWith('http')) src = '/' + src.replace(/^\/+/, '');
    const altAttr = alt ? ` alt="${alt}"` : '';
    return `<span class="ai-img-wrap"><img src="${src}"${altAttr} style="${widthStyle}" /></span>`;
  });
}
